/* eslint-disable @typescript-eslint/no-explicit-any */
import Client from "../shared/services/Client";
import Personalization from "./Personalization";
import Redirect from "../utils/Redirect";
import Cookie from "js-cookie";
import {
    SHARED_USER_ID_COOKIE_NAME,
    SCREENS,
    SOCIAL_MEDIA,
    REFERRAL_SHARING_PREFERENCES
} from "common/constants/Constants";
import appPath from "../utils/appPath";

interface Captcha {
    useRecaptcha: boolean;
    captchaResponse: any;
}

interface ProfessionInfo {
    industry?: string;
    jobTitle?: string;
    seniority?: string;
}

function hasUserToken(): boolean {
    return Boolean(Cookie.get(SHARED_USER_ID_COOKIE_NAME));
}

function checkPassword(password: string): Promise<any> {
    return Client.post("/api/check-password", {
        password
    });
}

function login(email: string, password: string, captcha?: Captcha): Promise<any> {
    return Client.post("/api/login/bconnect", {
        email,
        password,
        captcha
    });
}

function fetchUser(): Promise<any> {
    return Client.get("/api/account/current-user");
}

function sendEmailUpdateEmail(email: string): Promise<any> {
    return Client.put("/api/account/send-email-update-email", {
        newEmail: email
    });
}

function resendConfirmationEmail(email: string): Promise<any> {
    return Client.put("/api/account/resend-confirmation-email", {
        newEmail: email
    });
}

function resendBulkSubscriptionConfirmationEmail(email: string, resetPasswordValue: string): Promise<any> {
    return Client.put("/api/bulk-subscription-resend-confirmation-email", {
        email,
        resetPassword: resetPasswordValue
    });
}

function updatePassword(oldPassword: string, newPassword: string): Promise<any> {
    return Client.put("/api/account/update-password", {
        oldPassword,
        newPassword
    });
}

function updateRegion(region: string): Promise<any> {
    return Client.put("/api/account/update-region", {
        region
    });
}

function updateProfession(professionInfo: ProfessionInfo): Promise<any> {
    return Client.put("/api/account/update-profession", {
        professionInfo
    });
}

function updateName(firstName: string, lastName: string): Promise<any> {
    return Client.put("/api/account/update-name", {
        firstName,
        lastName
    });
}

function socialLogin(socialSite: SOCIAL_MEDIA): Promise<any> {
    return Client.post(`/api/login/${ socialSite }`);
}

function updateMarketingOptions(marketingOptionId: string, status: boolean): Promise<any> {
    return Client.put("/api/account/update-marketing-options", {
        marketingOptionId,
        status
    });
}

function updateReferralSharingPreference(status: REFERRAL_SHARING_PREFERENCES, ratePlanId: string): Promise<any> {
    const isOptedIn = status === REFERRAL_SHARING_PREFERENCES.OPT_IN;
    const path = `/api/referrals/opt-${ isOptedIn ? "in" : "out" }`;

    return Client.put(path, isOptedIn ? { productRatePlanId: ratePlanId } : {});
}

function logout(redirectURL = appPath.withoutQuery(SCREENS.SIGN_IN)): Promise<any> {
    Personalization.removeSessionId();

    return Client.post("/api/logout")
        .then(() => {
            Redirect.redirectTo(redirectURL);
        });
}

function forgotPassword(email: string, redirectUrl = "", captcha?: Captcha): Promise<any> {
    return Client.post("/api/generate-password-key", {
        email,
        redirectUrl,
        captcha
    });
}

function resetPassword(email: string, password: string, validationKey: string, isNewUser = false): Promise<any> {
    return Client.post("/api/reset-password", {
        email,
        password,
        validationKey,
        isNewUser
    });
}

function deactivateAccount(): Promise<any> {
    return Client.post("/api/account/deactivate-account");
}

function register(email: string, country?: string, captcha?: Captcha, redirectTo?: string): Promise<any> {
    return Client.post("/api/v3/register/bconnect", {
        email,
        country,
        captcha,
        redirectTo
    });
}

function registerWithSubscription(email: string, subscription: {[key: string]: any}): Promise<any> {
    const { product } = subscription;
    const endpoint = product === "BW" ? "businessweek" : "bbg";

    return Client.post(`/api/v3/register/${ endpoint }`, {
        email,
        subscription
    });
}

function updateEmail(updateEmailRedirectToken: string): Promise<any> {
    return Client.post("/api/account/update-email", { updateEmailRedirectToken });
}

function getEmailFromUpdateEmailToken(token: string): Promise<any> {
    return Client.get(`/api/get-email-from-update-email-token?token=${ token }`);
}

const User = {
    hasUserToken,
    checkPassword,
    login,
    logout,
    register,
    fetchUser,
    sendEmailUpdateEmail,
    resendConfirmationEmail,
    resendBulkSubscriptionConfirmationEmail,
    updatePassword,
    updateRegion,
    updateProfession,
    socialLogin,
    forgotPassword,
    resetPassword,
    updateMarketingOptions,
    updateReferralSharingPreference,
    deactivateAccount,
    registerWithSubscription,
    updateName,
    updateEmail,
    getEmailFromUpdateEmailToken
};

export default User;
